import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useAuth } from '../auth/AuthProvider';

function useAdminEnabled() {
  const { user, demo } = useAuth();
  return Boolean(user) && !demo && user?.rol === 'admin';
}

export function useUsuarios(search = '') {
  const enabled = useAdminEnabled();
  return useQuery({
    queryKey: ['usuarios', search],
    enabled,
    queryFn: async () => (await api.finanzas.usuarios.list({ search })).data,
  });
}

export function useCrearUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.finanzas.usuarios.crear(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios'] }),
  });
}

export function useActualizarUsuario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.finanzas.usuarios.actualizar(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios'] }),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.finanzas.usuarios.resetPassword(id, password),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['usuarios'] }),
  });
}

export function useImprentaEventos(filtros: {
  estado?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  const enabled = useAdminEnabled();
  return useQuery({
    queryKey: ['imprenta', 'eventos', filtros],
    enabled,
    queryFn: () => api.imprenta.list(filtros),
  });
}

export function useTfhkaEstado() {
  const enabled = useAdminEnabled();
  return useQuery({
    queryKey: ['imprenta', 'estado'],
    enabled,
    queryFn: async () => (await api.imprenta.estado()).data,
  });
}

export function useProbarTfhka() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.imprenta.probar(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['imprenta', 'estado'] });
    },
  });
}

export function useImprentaEvento(id: string | null) {
  const enabled = useAdminEnabled();
  return useQuery({
    queryKey: ['imprenta', 'evento', id],
    enabled: enabled && Boolean(id),
    queryFn: async () => (await api.imprenta.detail(id ?? '')).data,
  });
}
